import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  AfterViewInit,
  signal,
} from '@angular/core';
import { AuthService, UserService } from '@app/shared/services';
import { select, Store } from '@ngrx/store';
import * as loginSelectors from '../../../root-store/login-store/login.selector';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { MatSidenav } from '@angular/material/sidenav';
import { NetworkSetupNavService } from '@app/shared/services/network-setup-nav/network-setup-nav.service';
import { NetworkSetupService } from '@app/shared/services/network-setup/network-setup.service';
//
import { fetchUserAttributes } from '@aws-amplify/auth';
import { EventNames, EVENTS } from '@app/constants/events.constants';
import { getCurrentUserDetailsAll } from '../../../root-store/login-store/login.selector';
import { NetworkSetupTitleEnum } from './network-setup-enums';
import { NetworkHeadingComponent } from './components/network-heading/network-heading.component';
import { SetupComponent } from './components/family-members/family-setup.component';
import { ChatWidgetService } from '@app/shared/services/chat-widget/chat-widget.service';
import { UserTypeEnum } from '@app/enum/UserTypeEnum';
import { loginStoreActions } from '@app/root-store';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';
@AutoUnsubscribe()
@Component({
  selector: 'network-setup',
  templateUrl: './network-setup.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NetworkSetupComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() accountType;
  @ViewChild('sidenav') public sidenav: MatSidenav;
  @Output() closeAccountSetupSideBarEventEmitter = new EventEmitter();
  @ViewChild(SetupComponent) setupComponent;

  user$;
  showLoader = false;
  screenWidth: number;
  switchFromUser = false;
  subscription: Subscription = new Subscription();
  currentUser;

  steps: Array<{
    title: NetworkSetupTitleEnum;
    completed: boolean;
    status: 'inactive' | 'active';
    subText?: string;
    listHeader?: string;
    relationshipType?: string;
  }> = [];

  @Input() isImpersonating = false;
  resizeObservable$: Observable<Event>;
  currentActiveIndex = 0;
  currentActiveElement;
  markAsComplete = false;
  setupInstructions = [];
  videoSrc = 'https://www.loom.com/embed/3db82af9ba5d4e3a88bf2a7d772ffd14?sid=21c0c15d-26a1-4963-ae47-ea5d3825e71e';
  relationshipList = signal([]);
  constructor(
    private _userService: UserService,
    private store: Store,
    private cd: ChangeDetectorRef,
    private networkSetupNavService: NetworkSetupNavService,
    private networkSetupService: NetworkSetupService,
    private chatWidgetService: ChatWidgetService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.screenWidth = window.innerWidth;
    this.resizeObservable$ = fromEvent(window, 'resize');
    this.subscription.add(
      this.resizeObservable$.subscribe((evt) => {
        this.screenWidth = evt.target['innerWidth'];
      })
    );
    this.subscription.add(
      this.store.pipe(select(loginSelectors.getCurrentUserFullDetails)).subscribe(async (result) => {
        try {
          if (result) {
            this.isImpersonating = true;
          }
          const authUser = this.authService.user;
          if (result?.switch_from_user_id && result?.id != authUser['id']) {
            this.isImpersonating = true;
          } else {
            this.isImpersonating = false;
          }
          this.currentUser = result;
          const firstName = this.currentUser?.nick_name ?? this.currentUser?.first_name ?? this.currentUser?.first_middle_name;
          if (result.user_type == '' || result.user_type == UserTypeEnum.Standard) {
            this.steps = [
              { title: NetworkSetupTitleEnum.Instructions, completed: false, status: 'inactive' },
              {
                title: NetworkSetupTitleEnum.Suggestions,
                completed: false,
                status: 'inactive',
                subText:
                  'These users are connected to others within your network. If anyone should be added to your network, feel free to add them in this step.',
                listHeader: '',
              },
              {
                title: NetworkSetupTitleEnum.FamilyUsers,
                completed: false,
                status: 'inactive',
                subText: `List all the family and friends of ${firstName} who they will be collaborating with. If you’re unsure, you can always add users in the network area of this account later. You really just want to make sure that the lowest hanging fruit is covered in this step - like immediate family members (spouse, children, etc), and  other major role players (heirs, beneficiaries, joint owners, executors, etc).`,
                listHeader: '',
                relationshipType: 'Family Connections',
              },

              { title: NetworkSetupTitleEnum.Status, completed: false, status: 'inactive', subText: '', listHeader: '' },
            ];
          }
          if (result.user_type == UserTypeEnum.Professional) {
            this.steps = [
              { title: NetworkSetupTitleEnum.Instructions, completed: false, status: 'inactive' },
              {
                title: NetworkSetupTitleEnum.Clients,
                completed: false,
                status: 'inactive',
                subText:
                  'We suggest adding the clients who you plan on starting with in this step.  You  do not need to list all family members of a client yet. Typically, only the matriarch or patriarch should be added, and we will add the rest of their family members in a subsequent step. If they do not have an account already, select ‘create new user’.',
                listHeader: '',
                relationshipType: 'Clients',
              },
              {
                title: NetworkSetupTitleEnum.Professionals,
                completed: false,
                status: 'inactive',
                subText: `We suggest adding the clients who you plan on starting with in this step.  You  do not need to list all family members of a client yet. Typically, only the matriarch or patriarch should be added, and we will add the rest of their family members in a subsequent step. If they do not have an account already, select ‘create new user’.`,
                listHeader: '',
                relationshipType: 'Professional Connections',
              },

              { title: NetworkSetupTitleEnum.Status, completed: false, status: 'inactive', subText: '', listHeader: '' },
            ];
          }
          this.cd.detectChanges();
        } catch (ex) {}
      })
    );
    this.user$ = this.store.select(getCurrentUserDetailsAll);
    this.subscription.add(
      this.user$.subscribe((user) => {
        this.currentUser = user;
        this.cd.detectChanges();
      })
    );
    this.setupInstructions = [
      {
        image: 'assets/images/network.svg',
        title: 'About Network Setup',
        description: 'We walk you through how to set up your network inside Kinnect.',
        list: [
          `<span class="font-bold text-[#18191C]">Add People Easily</span> – Create profiles for family members or others you may collaborate with now or later. Nothing is permanent—adding, removing, or editing users is simple.`,
          '<span class="font-bold text-[#18191C]">Vaults Create Automatically</span> – Each person you add generates the right vaults automatically—personal, shared, and family.',
          '<span class="font-bold text-[#18191C]">Control Invitations</span> – Adding someone doesn’t invite them right away. You decide when to give access after your setup is complete.',
          '<span class="font-bold text-[#18191C]">Plan for the Future</span> – Even if you’re not sharing data yet, adding users early helps organize everything properly from the start.',
        ],
        note: 'Network setup is flexible, build it step by step as your data and family needs evolve.',
      },
      {
        image: 'assets/images/connection.svg',
        title: 'Who should you connect with?',
        list: [
          'Beneficiaries, executors, trustees or other role players within your estate plan.',
          'People who you co-owns assets, liabilities or legal entities with.',
          'People who manage some of your information on your behalf. ',
          'People who need to know details about what you have and where things are, either now or in the future. ',
          'People who would need access to your information if something happened to you.',
          'Other people who you need to understand what they have, where things are, etc. Either currently, and/or in the future.',
        ],
      },
      {
        image: 'assets/images/mind.svg',
        title: 'A few points to keep in mind:',
        list: [
          'Each user is in complete control of who they connect to, and how data is shared with others.',
          "Just being connected to someone doesn't automatically give them access to anything. ",
          'You can add or remove people from your network at any point in the future.',
        ],
      },
    ];
  }
  ngAfterViewInit() {
    this.steps[0].completed = false;
    this.setStatusForSidenavMenu(0);
    this.trackEventsInMixPanel([{ eventName: EVENTS.network_setup_instruction_start }]);
  }

  closeSideNav() {
    let events = [{ eventName: EVENTS.network_setup_close }];
    if (this.markAsComplete) events.push({ eventName: EVENTS.network_setup_status_complete });
    this.trackEventsInMixPanel(events);
    this.networkSetupService.handleSetupDataCollection.next([]);
    this.networkSetupNavService.handleNav(false);
    this.closeAccountSetupSideBarEventEmitter.emit(false);
  }

  ngOnDestroy(): void {
    this.markAsComplete = false;
    this.subscription.unsubscribe();
    this._userService.cleanupWorker();
  }
  handleSidenavMenuNavigation(index?: number) {
    if (!!index || index == 0) {
      this.setStatusForSidenavMenu(index);
    } else {
      this.setStatusForSidenavMenu();
    }
  }

  trackEventsInMixPanel(events: { eventName: EventNames; data?: any }[]) {
    this._userService.trackEventsInMixPanel(events).then((response) => {});
  }

  setStatusForSidenavMenu(activeIndex?: number) {
    if (activeIndex === undefined) {
      // Move to the next step
      if (this.currentActiveIndex !== undefined && this.currentActiveIndex < this.steps.length - 1) {
        activeIndex = this.currentActiveIndex + 1;
      } else {
        // If no more steps are available, do nothing or reset to the first step
        activeIndex = 0; // or handle this case based on your requirements
      }
    }

    if (activeIndex !== -1 && activeIndex < this.steps.length) {
      for (let i = 0; i < this.steps.length; i++) {
        if (i === activeIndex) {
          this.steps[i].status = 'active';
          this.currentActiveElement = this.steps[i];
        } else {
          this.steps[i].status = 'inactive';
        }
      }
      this.markAsComplete = false;
      this.currentActiveIndex = activeIndex;
      this.cd.detectChanges();
    }

    if (this.currentActiveIndex == 1) {
      this.trackEventsInMixPanel([{ eventName: EVENTS.network_setup_instruction_end }, { eventName: EVENTS.network_setup_immediate_family_start }]);
    } else if (this.currentActiveIndex == 2) {
      this.trackEventsInMixPanel([{ eventName: EVENTS.network_setup_immediate_family_end }, { eventName: EVENTS.network_setup_in_laws_start }]);
    } else if (this.currentActiveIndex == 3) {
      this.trackEventsInMixPanel([{ eventName: EVENTS.network_setup_professional_end }, { eventName: EVENTS.network_setup_professional_start }]);
    } else if (this.currentActiveIndex == 4) {
      this.trackEventsInMixPanel([{ eventName: EVENTS.network_setup_close }]);
    }
  }
  submitAllFormAnswers(value) {
    this.markAsComplete = !!value;
    if (!!value) {
      this.completeSetup();
    }
  }
  gotoPrevious() {
    if (this.currentActiveIndex > 0) {
      const exist = this.networkSetupService.handleSetupDataCollection.getValue().find((item) => item.step == this.currentActiveIndex);

      this.currentActiveIndex = this.currentActiveIndex - 1;

      for (let i = 0; i < this.steps.length; i++) {
        if (i === this.currentActiveIndex) {
          this.steps[i].status = 'active';
          this.currentActiveElement = this.steps[i];
        } else {
          this.steps[i].status = 'inactive';
        }
      }
      this.cd.detectChanges();
    }
  }
  handleOptionValueEmitter(value) {
    if (value == 'next') this.handleSidenavMenuNavigation();
  }
  completeSetup() {
    this.subscription.add(
      this.networkSetupNavService
        .updateNetworkStatus({
          status: 'complete',
          user_id: this.currentUser.id,
          type: 'admins',
        })
        .subscribe(() => {
          this.store.dispatch(new loginStoreActions.LoginRequestAction());
          this.closeSideNav();
        })
    );
  }

  handlerRelationshipList(list) {
    this.relationshipList.set(list);
  }
  handleSetupText(text: NetworkSetupTitleEnum) {
    if (text == NetworkSetupTitleEnum.Suggestions) {
      return `
      <span class="font-weight-bold-blue text-xl font-bold"
        >Network
        <span class="text-k-blue font-bold"> Suggestions</span>
      </span>
  `;
    }
    if (text == NetworkSetupTitleEnum.FamilyUsers) {
      return `
      <span class="font-weight-bold-blue text-xl font-bold"
        >
        <span class="text-k-blue font-bold">Family and Friends </span>
      </span>
    `;
    }
    if (text == NetworkSetupTitleEnum.Status) {
      return `
      <span class="font-weight-bold-blue text-xl font-bold"
        >Network
        <span class="text-k-blue font-bold"> Status </span>
      </span>
  `;
    }

    if (text == NetworkSetupTitleEnum.Instructions) {
      return `
      <span class="font-weight-bold-blue text-xl font-bold"
        >About
        <span class="text-k-blue font-bold"> Network Set up</span>
      </span>
  `;
    }
    if (text == NetworkSetupTitleEnum.ProfessionalUsers) {
      return `

      <span class="font-weight-bold-blue text-xl font-bold"
        >{{ (currentUser?.nick_name ?? currentUser?.first_name) + "'s" | titlecase }}
        <span class="text-k-blue font-bold">{{ text }} </span>
      </span>
  `;
    }
    if (text == NetworkSetupTitleEnum.Clients) {
      return `
      <span class="font-bold text-xl text-k-blue"
        >
      Clients
      </span>
    `;
    }
    if (text == NetworkSetupTitleEnum.Professionals) {
      return `
        <span class="text-k-blue font-bold  text-xl">Professional Connections </span>
    `;
    }
  }
  handleMoveToNextStep() {
    this.handleSidenavMenuNavigation();
  }
}
